import { TZDate } from '@repo/tzdate';
import { useMemo } from 'react';
import {
    OctoProduct,
    OctoCalendarRequest,
    OctoCalendarResponse,
    OctoAvailabilityRequest,
    OctoAvailabilityResponse,
    OctoNextAvailabilityRequest,
    OctoNextAvailabilityResponse,
    TicketOptionWithQuantity,
    OctoUnitQuantity,
    OctoOrder,
    OctoBookingConfirmRequest,
} from '@repo/types';
import useSWR from 'swr';
import {
    confirmOctoOrder,
    getOctoAvailability,
    getOctoCalendar,
    getOctoNextAvailability,
    getOctoOrder,
    getOctoProduct,
} from '../../fetchers/octo-api';
import {
    productInstancesFromOctoAvailabilityEntry,
    productInstancesFromOctoCalendarEntry,
} from '../../../OctoProductMapper';

export function useOctoProduct(productId?: string) {
    const cacheKey = productId ? `octo-product-${productId}` : null;

    const { data, isLoading, error } = useSWR<OctoProduct>(
        cacheKey,
        async () => {
            return await getOctoProduct(productId!);
        },
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );

    return {
        data,
        isLoading,
        error,
    };
}

export function useOctoOrder(orderId?: string) {
    const cacheKey = orderId ? `octo-order-${orderId}` : null;

    const { data, isLoading, error, mutate } = useSWR<OctoOrder>(
        cacheKey,
        async () => {
            return await getOctoOrder(orderId!);
        },
        {
            shouldRetryOnError: false,
            revalidateOnFocus: true,
        },
    );

    return {
        data,
        isLoading,
        error,
        mutate,
    };
}

export function useConfirmedOctoOrder(octoOrder?: OctoOrder) {
    const cacheKey =
        octoOrder && !octoOrder.confirmedAt ? `confirmed-octo-order-${octoOrder.id}` : null;

    const { data, isLoading, error, mutate } = useSWR<OctoOrder>(
        cacheKey,
        async () => {
            const request: OctoBookingConfirmRequest = {
                contact: octoOrder!.contact!,
            };

            return await confirmOctoOrder(octoOrder!.id, request);
        },
        {
            shouldRetryOnError: true,
            revalidateOnFocus: true,
            errorRetryInterval: 2000,
            errorRetryCount: 10,
        },
    );

    return {
        data,
        isLoading,
        error,
        mutate,
    };
}

export function useOctoCalendar(request?: OctoCalendarRequest) {
    const cacheKey = request ? `octo-calendar-${JSON.stringify(request)}` : null;

    const { data, isLoading, error } = useSWR<OctoCalendarResponse | undefined>(
        cacheKey,
        async () => {
            if (!request) return undefined;

            const response = await getOctoCalendar(request);
            return { request, response };
        },
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );

    return {
        data,
        isLoading,
        error,
    };
}

export function useOctoCalendars(requests: OctoCalendarRequest[]) {
    const cacheKey = requests.length > 0 ? `octo-calendars-${JSON.stringify(requests)}` : null;

    const { data, isLoading, error } = useSWR<OctoCalendarResponse[]>(
        cacheKey,
        async () => {
            const results = await Promise.all(
                requests.map(async (request) => {
                    const response = await getOctoCalendar(request);
                    return { request, response };
                }),
            );

            return results;
        },
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );

    return {
        data,
        isLoading,
        error,
    };
}

export function useOctoAvailabilities(
    from: TZDate | null,
    to: TZDate | null,
    octoProduct?: OctoProduct,
    quantities?: TicketOptionWithQuantity[],
    selectedOptionId?: string,
) {
    const availabilityRequests = useMemo(() => {
        if (!octoProduct || !from || !to) return [];

        // For products with multiple options, wait for selectedOptionId before fetching
        // This prevents fetching all options on initial load before auto-selection kicks in
        if (octoProduct.options.length > 1 && !selectedOptionId) {
            return [];
        }

        // If selectedOptionId is provided, only fetch for that option
        const optionsToFetch = selectedOptionId
            ? octoProduct.options.filter((opt) => opt.id === selectedOptionId)
            : octoProduct.options;

        const requests = optionsToFetch.map((option) => {
            const selectedQuantities = quantities?.filter((q) => q.quantity > 0) ?? [];

            const request = {
                productId: octoProduct.id,
                optionId: option.id,
                localDateStart: from.format('YYYY-MM-DD'),
                localDateEnd: to.format('YYYY-MM-DD'),
            } as OctoAvailabilityRequest;

            if (selectedQuantities.length === 0) {
                return request;
            }

            const quantitiesForOption = selectedQuantities.filter((q) =>
                option.units.some((u) => u.id === q.id),
            );

            if (quantitiesForOption.length === 0) return null;

            request.units = quantitiesForOption.map(
                (q) => ({ id: q.id, quantity: q.quantity }) as OctoUnitQuantity,
            );

            return request;
        });

        return requests.filter((request) => request !== null);
    }, [quantities, octoProduct, from, to, selectedOptionId]);

    const fetchCalendarEntries =
        octoProduct && availabilityRequests.find((request) => request.units === undefined);

    const { data: octoCalendarResponses } = useOctoCalendars(
        availabilityRequests && fetchCalendarEntries ? availabilityRequests : [],
    );
    const { data: octoAvailabilityResponses } = useOctoAvailabilitiesForRequests(
        !fetchCalendarEntries ? availabilityRequests : [],
    );

    return useMemo(() => {
        if (!octoProduct) return undefined;
        const calendarEntries = octoCalendarResponses?.map((response) => {
            return response.response.flatMap((x) =>
                productInstancesFromOctoCalendarEntry(x, response.request, octoProduct),
            );
        });

        const availabilitityEntries = octoAvailabilityResponses?.map((response) => {
            return response.response.flatMap((x) =>
                productInstancesFromOctoAvailabilityEntry(x, response.request, octoProduct),
            );
        });

        const allCalendarAvailabilities = calendarEntries?.flatMap((x) => x) ?? [];
        const allAvailabilityEntries = availabilitityEntries?.flatMap((x) => x) ?? [];

        return [...allCalendarAvailabilities, ...allAvailabilityEntries];
    }, [octoAvailabilityResponses, octoCalendarResponses, octoProduct]);
}

function useOctoAvailabilitiesForRequests(requests: OctoAvailabilityRequest[]) {
    const cacheKey = requests.length > 0 ? `octo-availabilities-${JSON.stringify(requests)}` : null;

    const { data, isLoading, error } = useSWR<OctoAvailabilityResponse[]>(
        cacheKey,
        async () => {
            const results = await Promise.all(
                requests.map(async (request) => {
                    const response = await getOctoAvailability(request);
                    return { request, response };
                }),
            );

            return results;
        },
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );

    return {
        data,
        isLoading,
        error,
    };
}

export function useOctoNextAvailability(request?: OctoNextAvailabilityRequest) {
    const cacheKey = request ? `octo-next-availability-${JSON.stringify(request)}` : null;

    const { data, isLoading, error } = useSWR<OctoNextAvailabilityResponse>(
        cacheKey,
        async () => {
            return await getOctoNextAvailability(request!);
        },
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );

    return {
        data,
        isLoading,
        error,
    };
}
